import ShopImages from '@activities/shops/ShopDetailBottomSheet/ShopImages';
import AuthGuard from '@components/AuthGuard';
import BottomSheetHeader from '@components/BottomSheetHeader';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { formatKoreanPhone } from '@constants/regexp';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useCopyShopAddress from '@hooks/shop/useCopyShopAddress';
import useShop from '@hooks/shop/useShop';
import useAppCustom from '@hooks/useAppCustom';
import Copy from '@icons/Copy';
import FavoriteFill from '@icons/favorite/FavoriteFill';
import PhoneFill from '@icons/phone/PhoneFill';
import { ShopStatus } from '@models/shop/common';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useTranslation } from 'react-i18next';

import useToggleMyShop from './hooks/useToggleMyShop';
import OrderButton from './OrderButton';

interface Params {
    shopId: string;
}

declare module '@stackflow/config' {
    interface Register {
        ShopDetailBottomSheet: Params;
    }
}

const ShopDetailBottomSheet:ActivityComponentType<'ShopDetailBottomSheet'> = ({ params }) => {
    const { shopId } = params;
    const { t } = useTranslation('shopDetail');
    const shop = useShop(shopId);
    const { toggleMyShop } = useToggleMyShop(shop);
    const copyAddress = useCopyShopAddress(shop?.address);
    const { useAddress, usePhoneNumber, useHashtag } = useAppCustom().shop;
    return (
        <BottomSheet>
            <Container>
                <BottomSheetHeader>
                    {t('header')}
                </BottomSheetHeader>
                <Column style={{ flex: 1, overflow: 'hidden' }}>
                    <ShopImages shop={shop} />
                    <Contents>
                        <Body fullWidth
                            shopStats={shop?.shopStatus}
                            spacing={4}>
                            <Row justify='space-between'
                                fullWidth>
                                <Title size='display'
                                    fontWeight='bold'>
                                    {shop?.name}
                                </Title>
                                <AuthGuard css={css`width: 28px;`}
                                    flows={[
                                        { activityName: 'ShopListActivity', activityParams: { type: 'LIST' } },
                                        { activityName: 'ShopDetailBottomSheet', activityParams: { shopId } },
                                    ]}>
                                    <FavoriteFill size={28}
                                        onClick={toggleMyShop}
                                        color={shop?.isMyShop ? colors.red : colors.gray4} />
                                </AuthGuard>
                            </Row>
                            {useAddress && (
                                <Text size='title'>
                                    {shop?.address}
                                </Text>
                            )}
                            <Row spacing={3}>
                                {useAddress && (
                                    <Row spacing={0.5} onClick={copyAddress}>
                                        <Copy size={16}
                                            color={colors.gray7}/>
                                        <Text color={colors.gray7}>{t('copyAddress')}</Text>
                                    </Row>
                                )}
                                {usePhoneNumber && (
                                    <a href={`tel:${shop?.phoneNumber}`}>
                                        <Row>
                                            <PhoneFill color={colors.gray7} size={16}/>
                                            <Text color={colors.gray7}>{t('callInquiry')}</Text>
                                        </Row>
                                    </a>
                                )}
                            </Row>
                            {useHashtag && (
                                <HashTags>
                                    {shop?.hashtag.map((tag, i) => (
                                        <Text key={`${tag} ${i}`} color={colors.gray6}>
                                            #{tag}
                                        </Text>
                                    ))}
                                </HashTags>
                            )}
                            <Divider/>
                            {(shop?.phoneNumber && usePhoneNumber) && (
                                <Row spacing={2}>
                                    <Text color={colors.primary} fontWeight='bold'>{t('shopPhone')}</Text>
                                    <Text>{formatKoreanPhone(shop.phoneNumber)}</Text>
                                </Row>
                            )}
                            {shop?.openhours && (
                                <Row spacing={2} align='flex-start'>
                                    <Text color={colors.primary} fontWeight='bold'>{t('openHours')}</Text>
                                    <OpenHoursText>
                                        {shop?.openhours}
                                    </OpenHoursText>
                                </Row>
                            )}
                        </Body>
                        <OrderButton shopId={shopId}
                            shopStatus={shop?.shopStatus || 'PENDING'}
                            status={shop?.orderStatus || 'CLOSED'} />
                    </Contents>
                </Column>
            </Container>
        </BottomSheet>
    );
};

const Container = styled.div`
    max-height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const Contents = styled.div`
    flex: 1;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Body = styled(Column)<{
    shopStats?: ShopStatus;
}>`
    padding: 20px 20px ${({ shopStats }) => (shopStats === 'NOT_USE_APP_ORDER' ? 28 : 120)}px;
    overflow-y: auto;
    flex: 1;
`;

const Title = styled(Text)`
    line-height: 36px;
`;

const HashTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
`;

const Divider = styled.div`
    background-color: var(--color-gray3);
    height: 1px;
    width: 100%;
`;

const OpenHoursText = styled(Text)`
    white-space: pre-wrap;
`;

export default ShopDetailBottomSheet;
